import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Download, ArrowRight, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [orderData, setOrderData] = useState(null);
  const { clearCart } = useCart();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = response.data;

        if (data.payment_status === 'paid') {
          setStatus('success');
          setOrderData(data);
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        
        if (attempts >= maxAttempts - 1) {
          setStatus('error');
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId, clearCart]);

  if (status === 'loading') {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="checkout-loading">
        <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center">
          <div className="spinner mx-auto mb-6 w-12 h-12 border-4" />
          <h1 className="font-serif text-3xl text-white mb-4">Processing Payment</h1>
          <p className="text-lg text-[#999999]">Please wait while we confirm your order...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="pt-28 bg-black min-h-screen" data-testid="checkout-success">
        <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Thank You for Your Purchase!
          </h1>
          <p className="text-lg text-[#999999] mb-8">
            Your order has been confirmed. Check your email for order details and download links.
          </p>

          {/* Order Details */}
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-8 mb-8 text-left">
            <h2 className="font-serif text-xl text-white mb-4">Order Summary</h2>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[#999999]">Total Paid:</span>
                <span className="text-white font-semibold">
                  €{(orderData?.amount_total / 100).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#999999]">Status:</span>
                <span className="text-green-600 font-semibold">Paid</span>
              </div>
            </div>
          </div>

          {/* Download Section */}
          <div className="bg-[#0f0f0f] border border-[#1a1a1a] p-8 mb-8">
            <Download className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <h3 className="font-serif text-xl text-white mb-2">Access Your Products</h3>
            <p className="text-sm text-[#999999] mb-4">
              Download links have been sent to your email. You can also access them from your account.
            </p>
            <Link to="/account">
              <Button 
                className="btn-hover bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none px-6 py-3 text-sm uppercase tracking-widest"
                data-testid="go-to-account-button"
              >
                Go to My Account
              </Button>
            </Link>
          </div>

          <Link to="/">
            <Button 
              variant="outline"
              className="btn-hover border-[#1A1A1A] text-white hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-4 text-sm uppercase tracking-widest"
              data-testid="back-home-button"
            >
              Back to Home
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Error or timeout state
  return (
    <div className="pt-28 bg-black min-h-screen" data-testid="checkout-error">
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 text-center">
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="font-serif text-3xl text-white mb-4">
          {status === 'timeout' ? 'Payment Verification Timeout' : 'Something Went Wrong'}
        </h1>
        <p className="text-lg text-[#999999] mb-8">
          {status === 'timeout' 
            ? 'We couldn\'t verify your payment. Please check your email for confirmation or contact support.'
            : 'There was an issue processing your payment. Please try again or contact support.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/cart">
            <Button 
              className="btn-hover bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-none px-8 py-4 text-sm uppercase tracking-widest"
            >
              Return to Cart
            </Button>
          </Link>
          <Link to="/contact">
            <Button 
              variant="outline"
              className="btn-hover border-[#1A1A1A] text-white hover:bg-[#1A1A1A] hover:text-white rounded-none px-8 py-4 text-sm uppercase tracking-widest"
            >
              Contact Support
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
